/* eslint-disable */

import * as React from 'react';
import { useEffect, useState, useContext } from 'react';
import { graphql } from 'gatsby';
import classNames from 'classnames';

import PDPTabs from '../../../components/PDPTabs';
import PDPImageForm from '../../../components/PDPImageForm';
import PDPSupplementFacts from '../../../components/PDPSupplementFacts';
import RecommendedProducts from '../../../components/PopularProducts/RecommendedProducts';
import SEO from '../../../components/SEO';
import { SearchProvider } from '../../../context/search-provider';
import ShopifyLayout from '../../../layout/ShopifyLayout';
import { StoreContext } from '../../../context/store-context';
import { AuthContext } from '../../../context/auth-context';
import { useRecommendedProducts } from '../../../utils/hooks';

const ns = `page-pdp`;

export default function Product({
  location,
  data: {
    product,
    product: {
      shortDescription,
      aboutSupportsHeadlines,
      aboutDescriptionSummary,
      aboutDescriptionPoints,
      suggestedUse,
      supplementFacts,
      shippingDescription,
    },
    wp: {
      acfOptionsPdp: {
        pdp: {
          shopPay, addToCartIcons,
        },
      },
    },
  },
}) {
  const {
    title,
    handle,
    seo,
    images: [firstImage],
    shopifyId,
  } = product;

  const rootClassnames = classNames({
    [`${ns}`]: true,
    [`${ns}-${handle}`]: true,
  });

  const { state: authState } = useContext(AuthContext);
  const { userPropertiesLoaded } = authState;
  const [elevarUserEventLoaded, setElevarUserEventLoaded] = useState(false);
  const { elevarUserDataEvent, cartLoading } = useContext(StoreContext);

  useEffect(() => {
    if (userPropertiesLoaded && !cartLoading) {
      elevarUserDataEvent(location);
      setElevarUserEventLoaded(true);
    }
  }, [userPropertiesLoaded, cartLoading]);

  const [recommendationsLoading, recommendedProducts] = useRecommendedProducts([shopifyId]);

  const handleRecommendedProductsLoaded = ({
    initialVisibleProducts,
  }) => {
    return initialVisibleProducts;
  };

  const recommendedProductsComponent = (
    <SearchProvider>
      <div className={'container'}>
        <RecommendedProducts
          title={recommendedProducts?.title}
          description={recommendedProducts?.description}
          productId={shopifyId}
          location={location}
          onRecommendedProductsLoaded={handleRecommendedProductsLoaded}
        />
      </div>
    </SearchProvider>
  );

  return (
    <ShopifyLayout location={location}>
      <div className={rootClassnames}>
        <SEO
          title={seo.title || title}
          image={firstImage?.image?.src}
          description={seo.description || ''}
          page={handle}
          link={location.href}
        />
        <div className={'container'}>
          <div className={`${ns}__box`}>
            <PDPImageForm
              product={product}
              shortDescription={shortDescription}
              location={location}
              shopPay={shopPay}
              addToCartIcons={addToCartIcons}
              useUrlParameter
              preselect1stAvailableVariant
              elevarUserEventLoaded={elevarUserEventLoaded}
            />
          </div>
        </div>
        <PDPTabs
          productName={title}
          aboutSupportsHeadlines={aboutSupportsHeadlines}
          aboutDescriptionSummary={aboutDescriptionSummary}
          aboutDescriptionPoints={aboutDescriptionPoints}
          suggestedUse={suggestedUse}
          shippingDescription={shippingDescription}
        />
        <PDPSupplementFacts
          supplementFacts={supplementFacts}
        />
        {recommendedProductsComponent}
      </div>
    </ShopifyLayout>
  );
}

export const query = graphql`
  query ($id: String!, $productType: String!) {
    product: shopifyProduct(id: { eq: $id }) {
      shopifyId
      title
      handle
      description
      productType
      productTypeSlug: gatsbyPath(filePath: "/products/{ShopifyProduct.productType}")
      tags
      metafields {
        key
        value
        namespace
      }
      priceRangeV2 {
        maxVariantPrice {
          amount
          currencyCode
        }
        minVariantPrice {
          amount
          currencyCode
        }
      }
      storefrontId
      vendor
      images: media {
        ... on ShopifyMediaImage {
          image {
            src
            gatsbyImageData(layout: CONSTRAINED, width: 640, aspectRatio: 1)
          }
        }
      }
      variants {
        availableForSale
        shopifyId
        storefrontId
        title
        price
        compareAtPrice
        sku
        barcode
        inventoryQuantity
        selectedOptions {
          name
          value
        }
        image {
          altText
          originalSrc
        }
        metafields {
          key
          value
          namespace
        }
      }
      options {
        name
        values
        shopifyId
      }
      seo {
        title
        description
      }

      ##############
      # METAFIELDS #
      ##############
      shortDescription: metafield(
        namespace: "product_information",
        key: "short_description"
      ) {
        value
      }
      aboutSupportsHeadlines: metafield(
        namespace: "about_tab",
        key: "supports_headlines"
      ) {
        value
      }
      aboutDescriptionSummary: metafield(
        namespace: "about_tab",
        key: "description_summary"
      ) {
        value
      }
      aboutDescriptionPoints: metafield(
        namespace: "about_tab",
        key: "description_bullet_points"
      ) {
        value
      }
      suggestedUse: metafield(
        namespace: "suggested_use_tab",
        key: "suggested_use"
      ) {
        value
      }
      supplementFacts: metafield(
        namespace: "supplement_facts_tab",
        key: "supplement_facts_table"
      ) {
        value
      }
      shippingDescription: metafield(
        namespace: "shipping_tab",
        key: "shipping_description"
      ) {
        value
      }
    }
    suggestions: allShopifyProduct(
      limit: 3
      filter: { productType: { eq: $productType }, id: { ne: $id } }
    ) {
      nodes {
        ...ProductCard
      }
    }
    wp {
      acfOptionsPdp {
        pdp {
          shopPay {
            text
            logo
            linkText
            modal {
              title
              description
              list {
                icon
                label
              }
              footnote
              footer
            }
          }
          addToCartIcons {
            icons {
              icon
              label
            }
          }
        }
      }
    }
  }
`;
